import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios, { API, authHeaders, errText } from '../api';
import Layout from './Layout';
import { ui, statusBadge } from '../ui';
import { colors } from '../theme';
import DataTable from '../components/DataTable';
import PhotoLightbox from '../components/PhotoLightbox';
import MatchesBlock from '../components/MatchesBlock';
import QuestionnaireBlock from '../components/QuestionnaireBlock';
import SparkTransactionsBlock from '../components/SparkTransactionsBlock';
import { STATUS_LABEL, STATUS_TOOLTIP } from '../statusLabels';

export default function Clients() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [mainPhotoId, setMainPhotoId] = useState(null);
  const [error, setError] = useState('');
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [meetingLink, setMeetingLink] = useState('');
  const [meetingSaving, setMeetingSaving] = useState(false);
  const [note, setNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchItems = useCallback(() => {
    axios.get(`${API}/curator/clients`, {
      headers: authHeaders(),
      params: { search: search || undefined },
    }).then(r => setItems(r.data));
  }, [search]);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openClient = (c) => {
    setSelected(c); setMainPhotoId(null); setError('');
    setMeetingLink(c.current_slot?.meeting_link || '');
    setNote(c.note || ''); setNoteSaved(false);
  };

  const saveNote = async () => {
    setNoteSaving(true);
    try {
      const r = await axios.put(`${API}/curator/clients/${selected.id}/note`, { text: note }, { headers: authHeaders() });
      setSelected(r.data);
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 2000);
    } catch (e) {
      setError(errText(e, 'Не удалось сохранить заметку'));
    }
    setNoteSaving(false);
  };

  const saveMeetingLink = async () => {
    if (!selected?.current_slot) return;
    setMeetingSaving(true);
    try {
      await axios.patch(
        `${API}/curator/slots/${selected.current_slot.id}/meeting-link`,
        { meeting_link: meetingLink || null },
        { headers: authHeaders() },
      );
      await refreshSelected();
    } catch (e) {
      setError(errText(e, 'Не удалось сохранить ссылку'));
    }
    setMeetingSaving(false);
  };

  // Список отдаёт краткие данные (без метчей/подробной анкеты) — при открытии карточки
  // всегда дозапрашиваем полную деталь, а не используем строку из таблицы как есть.
  const openClientById = (id) => {
    axios.get(`${API}/curator/clients/${id}`, { headers: authHeaders() })
      .then(r => openClient(r.data))
      .catch(() => {});
  };

  // Переход по ссылке "Перейти в анкету" из поп-апа слота (Slots.js) — открывает
  // клиента напрямую по id из query-параметра, не дожидаясь совпадения со списком.
  useEffect(() => {
    const openId = searchParams.get('open');
    if (!openId) return;
    openClientById(openId);
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const activate = async () => {
    if (!mainPhotoId) { setError('Выберите главное фото'); return; }
    setError('');
    try {
      await axios.post(`${API}/curator/clients/${selected.id}/activate`, { main_photo_id: mainPhotoId }, { headers: authHeaders() });
      setSelected(null);
      fetchItems();
    } catch (e) {
      setError(errText(e, 'Не удалось активировать'));
    }
  };

  const block = async () => {
    if (!window.confirm(`Заблокировать анкету ${selected.first_name || selected.phone}?`)) return;
    await axios.post(`${API}/curator/clients/${selected.id}/block`, {}, { headers: authHeaders() });
    setSelected(null);
    fetchItems();
  };

  const refreshSelected = async () => {
    const r = await axios.get(`${API}/curator/clients/${selected.id}`, { headers: authHeaders() });
    setSelected(r.data);
  };

  const addPhoto = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    const form = new FormData();
    form.append('file', file);
    try {
      await axios.post(`${API}/curator/clients/${selected.id}/photos`, form, { headers: authHeaders() });
      refreshSelected();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось загрузить фото');
    }
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm('Точно удалить это фото? Восстановить его будет нельзя.')) return;
    setError('');
    try {
      await axios.delete(`${API}/curator/clients/${selected.id}/photos/${photoId}`, { headers: authHeaders() });
      refreshSelected();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось удалить фото');
    }
  };

  const setMainPhoto = async (photoId) => {
    setError('');
    try {
      await axios.post(`${API}/curator/clients/${selected.id}/photos/${photoId}/set-main`, {}, { headers: authHeaders() });
      refreshSelected();
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось сделать фото главным');
    }
  };

  if (selected) {
    return (
      <Layout>
        <button style={ui.btnSecondary} onClick={() => setSelected(null)}>← Все клиенты</button>
        <h1 style={{ ...ui.title, marginTop: 20 }}>{selected.first_name || 'Без имени'}, {selected.phone}</h1>

        <div style={ui.card}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, fontSize: 14 }}>
            <div><b>ID:</b> {selected.id}</div>
            <div><b>Статус:</b> {STATUS_LABEL[selected.status] || selected.status}</div>
            <div><b>Пол:</b> {selected.gender || '—'}</div>
            <div><b>Город:</b> {selected.city || '—'}</div>
            <div><b>Рост:</b> {selected.height || '—'}</div>
            <div><b>Семейное положение:</b> {selected.marital_status || '—'}</div>
            <div><b>Искры:</b> {selected.spark_balance}</div>
            {selected.referred_by_phone && (
              <div><b>Пригласил:</b> {selected.referred_by_name || 'Без имени'}, {selected.referred_by_phone}</div>
            )}
          </div>
          {selected.short_bio && <div style={{ marginTop: 12 }}><b>О себе:</b> {selected.short_bio}</div>}
        </div>

        {selected.status === 'awaiting_interview' && selected.current_slot && (
          <div style={ui.card}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Видео-звонок</div>
            <div style={{ color: colors.textMuted, fontSize: 13, marginBottom: 12 }}>
              Запись на {new Date(selected.current_slot.start_at).toLocaleString('ru', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}.
              Вставьте ссылку на Яндекс.Телемост — пользователь увидит кнопку «Подключиться» за 15 минут до звонка.
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                style={{ ...ui.input, flex: 1 }}
                value={meetingLink}
                onChange={(e) => setMeetingLink(e.target.value)}
                placeholder="https://telemost.yandex.ru/j/..."
              />
              <button style={ui.btn} onClick={saveMeetingLink} disabled={meetingSaving}>
                {meetingSaving ? 'Сохранение…' : 'Сохранить'}
              </button>
            </div>
          </div>
        )}

        {selected.status !== 'deleted' && (
        <div style={ui.card}>
          {(() => { const activating = selected.status === 'awaiting_interview' || selected.status === 'no_show'; return (
          <>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            Фото {activating ? '— нажмите на фото, чтобы выбрать главное для активации' : ''}
          </div>
          <div style={{ color: colors.textMuted, fontSize: 13, marginBottom: 12 }}>
            Главное фото отмечено золотой рамкой и подписью «Главное» — его нельзя удалить, пока оно главное.
            Чтобы заменить, сделайте главным другое фото или загрузите новое.
          </div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {selected.photos.length === 0 && <div style={ui.empty}>Фото не загружены</div>}
            {selected.photos.map(p => (
              <div key={p.id} style={{ width: 120, flexShrink: 0 }}>
                <div
                  onClick={() => activating && setMainPhotoId(p.id)}
                  style={{
                    width: 120, height: 120, borderRadius: 8, overflow: 'hidden', cursor: activating ? 'pointer' : 'default',
                    border: `3px solid ${mainPhotoId === p.id || p.is_main ? colors.accent : colors.border}`,
                    position: 'relative',
                  }}
                >
                  <img src={`${API}${p.url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  {p.is_main && <div style={{ position: 'absolute', bottom: 4, left: 4, right: 4, background: colors.accent, color: colors.bg, fontSize: 10, fontWeight: 700, textAlign: 'center', borderRadius: 4, padding: '2px 0' }}>Главное</div>}
                  <button
                    onClick={(e) => { e.stopPropagation(); setLightboxUrl(`${API}${p.url}`); }}
                    title="Открыть на весь экран"
                    style={{
                      position: 'absolute', top: 4, right: 4, width: 24, height: 24, borderRadius: 6,
                      background: 'rgba(6,10,22,0.6)', border: 'none', color: colors.text, fontSize: 13, cursor: 'zoom-in',
                    }}
                  >⤢</button>
                </div>
                {!activating && (
                  <div style={{ display: 'flex', gap: 4, marginTop: 6, fontSize: 11 }}>
                    {!p.is_main && (
                      <button style={{ ...ui.btnSecondary, padding: '4px 6px', fontSize: 11 }} onClick={() => setMainPhoto(p.id)}>Сделать главным</button>
                    )}
                    {!p.is_locked && (
                      <button style={{ ...ui.btnDanger, padding: '4px 6px', fontSize: 11 }} onClick={() => deletePhoto(p.id)}>Удалить</button>
                    )}
                  </div>
                )}
              </div>
            ))}
            <label style={{
              width: 120, height: 120, borderRadius: 8, border: `2px dashed ${colors.border}`, flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', textAlign: 'center',
              fontSize: 12, color: colors.textMuted, padding: 8,
            }}>
              + Добавить фото
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={addPhoto} style={{ display: 'none' }} />
            </label>
          </div>
          <PhotoLightbox url={lightboxUrl} onClose={() => setLightboxUrl(null)} />

          {error && <div style={{ ...ui.error, marginTop: 16 }}>{error}</div>}

          {activating && (
            <div style={{ marginTop: 16 }}>
              <button style={ui.btn} onClick={activate}>Активировать анкету</button>{' '}
              <button style={ui.btnDanger} onClick={block}>Отклонить и заблокировать</button>
            </div>
          )}
          </>
          ); })()}
        </div>
        )}

        <MatchesBlock userId={selected.id} matches={selected.matches} profilePath="/clients" />
        <SparkTransactionsBlock transactions={selected.spark_transactions} />

        <div style={ui.card}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Заметки</div>
          <div style={{ color: colors.textMuted, fontSize: 13, marginBottom: 12 }}>
            Личные, видны только вам — ни супер-админ, ни другие кураторы их не видят.
          </div>
          <textarea
            style={{ ...ui.input, width: '100%', minHeight: 100, resize: 'vertical', fontFamily: 'inherit' }}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Например: любит долгие звонки, попросил не звонить по утрам…"
          />
          <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
            <button style={ui.btn} onClick={saveNote} disabled={noteSaving}>
              {noteSaving ? 'Сохранение…' : 'Сохранить'}
            </button>
            {noteSaved && <span style={{ fontSize: 12, color: colors.good }}>Сохранено</span>}
          </div>
        </div>

        {selected.status !== 'deleted' && <QuestionnaireBlock questionnaire={selected.questionnaire} />}
      </Layout>
    );
  }

  return (
    <Layout>
      <h1 style={ui.title}>Клиенты</h1>
      <div style={{ ...ui.row, marginBottom: 12 }}>
        <input
          style={ui.input}
          placeholder="Поиск по имени, телефону или ID"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>
      <div style={ui.card}>
        <DataTable
          rows={items}
          rowKey={c => c.id}
          onRowClick={c => openClientById(c.id)}
          emptyText="К вам ещё никто не записался на интервью"
          columns={[
            { key: 'id', label: 'ID' },
            { key: 'first_name', label: 'Имя', render: c => c.first_name || '—' },
            { key: 'phone', label: 'Телефон' },
            {
              key: 'status',
              label: <span title={STATUS_TOOLTIP}>Статус ⓘ</span>,
              sortValue: c => STATUS_LABEL[c.status] || c.status,
              render: c => {
                const [bg, fg] = statusBadge[c.status] || [colors.surface2, colors.textMuted];
                return <span style={ui.badge(bg, fg)}>{STATUS_LABEL[c.status] || c.status}</span>;
              },
            },
            { key: 'spark_balance', label: 'Искры' },
            { key: 'matches_count', label: 'Метчи' },
            {
              key: 'created_at', label: 'Регистрация',
              sortValue: c => c.created_at,
              render: c => new Date(c.created_at).toLocaleDateString('ru', { day: '2-digit', month: '2-digit', year: 'numeric' }),
            },
          ]}
        />
      </div>
    </Layout>
  );
}
